<script setup lang="ts">
import type { BuffInfoType } from '@/types/fight'
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import FightSkillInfo from './FightSkillInfo.vue'
import FightBuffInfo from './FightBuffInfo.vue'
import FightRealAttr from './FightRealAttr.vue'

const AUTO_SCROLL_RESUME_DELAY = 30 * 1000

const userStore = useUserStore()

const props = withDefaults(
  defineProps<{
    realUserAttr: any
    buffInfoList: BuffInfoType[]
    debuffInfoList: BuffInfoType[]
    fightMessageList: string[]
    mp: number
    cdList: [number, number][]
    messageBoxId: string
    autoScroll?: boolean
  }>(),
  {
    autoScroll: true,
  },
)

const messageBoxRef = ref<HTMLDivElement>()
const isAutoScrollPaused = ref(false)
const resumeAutoScrollTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const clearResumeAutoScrollTimer = () => {
  if (!resumeAutoScrollTimer.value) return
  clearTimeout(resumeAutoScrollTimer.value)
  resumeAutoScrollTimer.value = null
}

const resumeAutoScroll = () => {
  const messageBoxEl = messageBoxRef.value
  if (!messageBoxEl) return
  isAutoScrollPaused.value = false
  messageBoxEl.scrollTo({
    top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
    behavior: 'smooth',
  })
}

const scheduleResumeAutoScroll = () => {
  clearResumeAutoScrollTimer()
  resumeAutoScrollTimer.value = setTimeout(() => {
    resumeAutoScroll()
  }, AUTO_SCROLL_RESUME_DELAY)
}

const handleMessageScroll = () => {
  const messageBoxEl = messageBoxRef.value
  if (!messageBoxEl) return

  const distanceToBottom = messageBoxEl.scrollHeight - messageBoxEl.clientHeight - messageBoxEl.scrollTop
  const isAtBottom = distanceToBottom <= 2

  if (isAtBottom) {
    isAutoScrollPaused.value = false
    clearResumeAutoScrollTimer()
    return
  }

  isAutoScrollPaused.value = true
  scheduleResumeAutoScroll()
}

watch(
  () => props.fightMessageList.length,
  async () => {
    const messageBoxEl = messageBoxRef.value
    if (!messageBoxEl) return

    if (!props.autoScroll || isAutoScrollPaused.value) return

    await nextTick()

    messageBoxEl.scrollTo({
      top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
      behavior: 'smooth',
    })
  },
)

onBeforeUnmount(() => {
  clearResumeAutoScrollTimer()
})
</script>

<template>
  <div class="fight-res-info-wrap">
    <h4>战斗信息</h4>
    <FightRealAttr :real-user-attr="realUserAttr" />
    <FightBuffInfo :buff-info-list="buffInfoList" :debuff-info-list="debuffInfoList" v-if="userStore.soulType > 3" />
    <div ref="messageBoxRef" class="fight-res-info affix" :id="messageBoxId" @scroll="handleMessageScroll">
      <p v-for="(item, index) of fightMessageList" :key="index" v-html="item"></p>
    </div>
    <FightSkillInfo :mp="mp" :cd-list="cdList" v-if="userStore.soulType > 3" />
  </div>
</template>

<style lang="scss" scoped>
.fight-res-info-wrap {
  border: 2px var(--fontColor) solid;
  border-radius: 5px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  position: relative;

  .fight-res-info {
    flex: 1;
    max-height: 100%;
    overflow: auto;
    padding: 0 20px 20px 20px;

    p {
      font-size: 14px;
      margin: 4px 0;
    }
  }
}
</style>
